/**
 * The open book, as the drawer reads it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TRADES, ACROSS EVERY ACCOUNT
 *
 * `GET /trades/open` takes no `connectionId` on purpose — see the service's
 * header. The account selector in the terminal header decides where the NEXT
 * order goes; it never decided what exists, and a trader with two brokers was
 * being shown half their risk.
 *
 * VALIDATED AT THE BOUNDARY. `api.get<T>` is a type ASSERTION, not a check:
 * the response is whatever the wire delivered, and a shape that changed under
 * us reaches `.map` and takes the whole drawer down — positions, orders and
 * the day tile with it. The panel a trader checks when something has gone
 * wrong must not be the thing that breaks.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useState } from 'react'
import { api } from '@/lib/api'
import type { LegRole, ProtectionSpec } from '@apna/shared'

export interface OpenBookLeg {
  tradeLegId: string
  seq: number
  /** `PENDING | FILLED | FAILED | CLOSING | CLOSED`. */
  status: string
  token: number | null
  side: string
  optionType: string
  strike: number
  expiry: string
  symbol: string
  lots: number
  lotSize: number
  qty: number
  /** Signed UNITS held. Zero on a leg that never filled. */
  netQty: number
  avgPrice: number
  realisedPnl: number
  chargesTotal: number
  /** When this leg last filled. Null on one that never did. */
  lastFillAt: string | null
  /** 7.5. What this leg is armed with. Null means none, which is a choice. */
  slSpec: ProtectionSpec | null
  tgtSpec: ProtectionSpec | null
  orphanedQty: number | null
  /** 9.10. What this leg is FOR. Null is NOT STATED, never `MAIN`. */
  role: LegRole | null
}

export interface OpenBookTrade {
  premiumProtection?: { stopLoss: number | null; target: number | null; state: string; reason: string | null }
  tradeId: string
  status: string
  mode: string
  underlying: string
  openedAt: string
  strategyId: string | null
  strategyName: string | null
  /** 4.18. Its own name when no strategy gives it one. See `isGrouped`. */
  label: string | null
  broker: { connectionId: string; label: string; mode: string }
  legs: OpenBookLeg[]
}

export type OpenBookState =
  | { readonly kind: 'LOADING' }
  | { readonly kind: 'READY'; readonly trades: readonly OpenBookTrade[] }
  /** The read failed. NOT an account with nothing in it. */
  | { readonly kind: 'FAILED'; readonly why: string }

const isLeg = (v: unknown): v is OpenBookLeg =>
  typeof v === 'object' &&
  v !== null &&
  typeof (v as OpenBookLeg).tradeLegId === 'string' &&
  typeof (v as OpenBookLeg).netQty === 'number' &&
  typeof (v as OpenBookLeg).avgPrice === 'number'

const isTrade = (v: unknown): v is OpenBookTrade => {
  if (typeof v !== 'object' || v === null) return false
  const t = v as OpenBookTrade
  return (
    typeof t.tradeId === 'string' &&
    typeof t.status === 'string' &&
    Array.isArray(t.legs) &&
    typeof t.broker === 'object' &&
    t.broker !== null &&
    typeof t.broker.label === 'string'
  )
}

export function useOpenBook(refreshToken: number): {
  state: OpenBookState
  /** Re-read now — after an exit, without waiting for the caller. */
  refresh: () => void
  /** Update one leg in place, after its protection is saved. */
  patchLeg: (tradeLegId: string, patch: Partial<OpenBookLeg>) => void
  /** Insert or replace one trade, as pushed. Returns false if it was not READY. */
  upsertTrade: (trade: OpenBookTrade) => boolean
  /** Drop one trade — it reached CLOSED. Returns false if it was not READY. */
  removeTrade: (tradeId: string) => boolean
} {
  const [state, setState] = useState<OpenBookState>({ kind: 'LOADING' })
  const [nonce, setNonce] = useState(0)
  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  useEffect(() => {
    let cancelled = false
    void api
      .get<unknown>('/trades/open')
      .then((r) => {
        if (cancelled) return
        const rows = (r as { trades?: unknown })?.trades
        if (!Array.isArray(rows)) {
          setState({
            kind: 'FAILED',
            why: 'the open book came back in a shape this build does not understand',
          })
          return
        }
        setState({
          kind: 'READY',
          // A malformed trade is dropped rather than failing the whole read:
          // the other baskets are real positions and the trader needs them.
          trades: rows.filter(isTrade).map((t) => ({ ...t, legs: t.legs.filter(isLeg) })),
        })
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setState({
          kind: 'FAILED',
          why: e instanceof Error ? e.message : 'the open book could not be read',
        })
      })
    return () => {
      cancelled = true
    }
  }, [refreshToken, nonce])

  /*
   * Patched in place rather than re-fetched. Arming one leg changes one
   * field; re-reading the whole book would rebuild the cards and close the
   * panel the trader is still typing in.
   */
  const patchLeg = useCallback((tradeLegId: string, patch: Partial<OpenBookLeg>) => {
    setState((s) =>
      s.kind !== 'READY'
        ? s
        : {
            kind: 'READY',
            trades: s.trades.map((t) => ({
              ...t,
              legs: t.legs.map((l) => (l.tradeLegId === tradeLegId ? { ...l, ...patch } : l)),
            })),
          },
    )
  }, [])

  /**
   * Apply a trade the server pushed.
   *
   * REPLACED WHOLE, not merged. The pushed object is built by the same
   * `openBookFor` the fetch uses, so it is the complete current truth for
   * that trade — merging would mean inventing a rule for which half wins,
   * and the server's is newer by construction.
   *
   * Refuses while the book is not READY. There is nothing to insert into,
   * and the caller falls back to a fetch, which is what a first load is
   * anyway.
   */
  const upsertTrade = useCallback((trade: OpenBookTrade): boolean => {
    let applied = false
    setState((s) => {
      if (s.kind !== 'READY') return s
      applied = true
      const i = s.trades.findIndex((t) => t.tradeId === trade.tradeId)
      // Newest first, matching the server's own ordering for a new card.
      const trades = i === -1 ? [trade, ...s.trades] : s.trades.map((t, n) => (n === i ? trade : t))
      return { kind: 'READY', trades }
    })
    return applied
  }, [])

  const removeTrade = useCallback((tradeId: string): boolean => {
    let applied = false
    setState((s) => {
      if (s.kind !== 'READY') return s
      applied = true
      return { kind: 'READY', trades: s.trades.filter((t) => t.tradeId !== tradeId) }
    })
    return applied
  }, [])

  return { state, refresh, patchLeg, upsertTrade, removeTrade }
}

/** Every token in the book, for one tick subscription. */
export function tokensOf(trades: readonly OpenBookTrade[]): number[] {
  const out = new Set<number>()
  for (const t of trades) {
    for (const l of t.legs) if (l.token !== null && l.token > 0) out.add(l.token)
  }
  return [...out]
}

/** Does this trade hold anything at all right now? */
export const holdsSomething = (t: OpenBookTrade): boolean =>
  t.legs.some((l) => l.netQty !== 0)

/**
 * A leg the broker never accepted.
 *
 * It holds nothing, it has no entry price, and the reason it failed is in
 * the ORDERS book where someone asking "why" will look.
 */
const FAILED = 'FAILED'

/**
 * Drop legs that never reached the broker — 9.28.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A FAILED LEG IS NOT A POSITION
 *
 * A failed leg was drawn in the pane beside the ones that filled, carrying
 * the quantity that was ORDERED and an empty entry price. On a two-legged
 * structure that reads as a hedge which does not exist: a short straddle
 * whose call was refused is a naked short put, and the pane was still
 * calling it a straddle.
 *
 * It never contributed to OPEN RISK — `holdsSomething` and `useOpenRisk`
 * both ask `netQty`, which a failed leg leaves at zero — so this is about
 * what the trader is shown, not about the arithmetic underneath.
 *
 * A trade left with NO legs is dropped entirely rather than drawn empty.
 * It would have landed in `unopened` anyway, since a trade that holds
 * nothing is not a position; returning it with an empty array would just
 * give the pane a card with no rows to draw.
 *
 * The count is still reachable: the header's REJECTED tally and the orders
 * book both still carry it, which is where a refusal belongs.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function withoutFailedLegs(
  trades: readonly OpenBookTrade[],
): readonly OpenBookTrade[] {
  // Identity when there is nothing to strip — the common case, and the
  // callers memoise on this array.
  if (!trades.some((t) => t.legs.some((l) => l.status === FAILED))) return trades

  const out: OpenBookTrade[] = []
  for (const t of trades) {
    const legs = t.legs.filter((l) => l.status !== FAILED)
    if (legs.length === 0) continue
    out.push(legs.length === t.legs.length ? t : { ...t, legs })
  }
  return out
}

/**
 * Was this trade placed as a UNIT, or is it one loose click? — ROADMAP 4.18.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE QUESTION THE DRAWER WAS ACTUALLY ASKING
 *
 * Three places used to ask `strategyId === null` and mean this — the split
 * below, and both drag rules in `legDrop`. That was a PROXY, and it held only
 * because every chain click was exactly one leg. Sending both sides of a
 * strike in one click breaks it: the trade has no strategy and is still very
 * much a group.
 *
 * `label` is the other half of the answer, and a stored one on purpose. The
 * leg COUNT would have done the job at entry and then quietly undone it:
 * `openBookFor` drops closed legs, so exiting one side of a straddle takes a
 * two-leg trade down to one and the group dissolves while the trader is
 * still in it.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export const isGrouped = (t: OpenBookTrade): boolean =>
  t.strategyId !== null || (t.label ?? null) !== null

/** The card header: the strategy that fired it, else the name it was given. */
export const tradeName = (t: OpenBookTrade): string | null =>
  t.strategyName ?? t.label ?? null

export interface SplitBook {
  /** Held baskets, as cards. */
  baskets: OpenBookTrade[]
  /** Held chain clicks, as rows. */
  manual: OpenBookTrade[]
  /** Open on the record and holding nothing: a refused entry, or still filling. */
  unopened: OpenBookTrade[]
}

/**
 * Split the book the way the drawer shows it.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A POSITION IS SOMETHING YOU HOLD, NOT A ROW THAT IS NOT CLOSED
 *
 * Measured against the real book on 20 Oct 2026, this account had 77 trades
 * whose status was not CLOSED and ZERO open positions at the broker. Drawing
 * a card for each would have buried the three that mattered under
 * seventy-four that did not — which is the failure the old netted pane had,
 * in the opposite direction.
 *
 * So the three groups are by what the trader can DO about each:
 *
 *   baskets   a position placed as a UNIT — a strategy, or both sides of a
 *             strike in one click: a card, with an exit on every leg
 *   manual    ONE loose leg taken from the chain: a row, same exits
 *   unopened  an entry that was refused, or one still filling. It is not a
 *             position and never was; its rejection is in the ORDERS book,
 *             which is where someone asking "why" will look.
 *
 * THE ONLY QUESTION ASKED IS `netQty`. There was a fourth group — trades the
 * 7.8 sweep had flagged NEEDS_REVIEW — and it was removed on 26 Oct 2026
 * because it ghosted real positions: a leg the user opened HERE disappeared
 * from the one screen meant to show what they opened here, leaving a number
 * in the footer and no way to act on it.
 *
 * The flag is still right and still shown — `TradeCards.legFlag` marks the
 * single leg the broker does not report, and the exit stays enabled because
 * `planExitByLeg` sizes from our own record, not the broker's netted row. A
 * disagreement is a FACT ABOUT A LEG, never grounds to hide the trade.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function splitBook(trades: readonly OpenBookTrade[]): SplitBook {
  const out: SplitBook = { baskets: [], manual: [], unopened: [] }
  for (const t of trades) {
    if (!holdsSomething(t)) out.unopened.push(t)
    else if (!isGrouped(t)) out.manual.push(t)
    else out.baskets.push(t)
  }
  return out
}

/**
 * A pushed trade, checked rather than trusted.
 *
 * It crossed a process boundary and a Redis channel. TypeScript saying it is
 * an `OpenBookTrade` is not evidence, and a malformed one would put a card on
 * screen with no legs, no symbol and a blank P&L — worse than the round trip
 * it saves. Anything unrecognised returns null, the caller declines, and the
 * drawer re-reads, which is the behaviour that was there before.
 *
 * Deliberately shallow: identity and the leg array. It does not re-validate
 * every number, because the server built this with the same code the fetch
 * uses — the question is "is this the shape", not "is this arithmetic right".
 */
export function asOpenBookTrade(raw: unknown): OpenBookTrade | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const t = raw as Partial<OpenBookTrade>
  if (typeof t.tradeId !== 'string' || t.tradeId === '') return null
  if (typeof t.status !== 'string') return null
  if (!Array.isArray(t.legs)) return null
  for (const l of t.legs) {
    if (!l || typeof l !== 'object') return null
    const leg = l as Partial<OpenBookLeg>
    if (typeof leg.tradeLegId !== 'string' || leg.tradeLegId === '') return null
    if (typeof leg.netQty !== 'number' || !Number.isFinite(leg.netQty)) return null
  }
  return t as OpenBookTrade
}
